import type { UseFormReturn } from 'react-hook-form';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Checkbox, Labeled, Select, Stack, TextInputField } from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { ISearchCommunityFormBodyValues } from './SearchCommunityFormBody';
import { FormField } from '~/components/atoms/FormField';
import { FormSection } from '~/components/atoms/FormSection';
import { ArticleSelector } from '~/components/organisms/fields/ArticleSelector';
import { CategorySelector } from '~/components/organisms/fields/CategorySelector';
import { UserBadgeSelector } from '~/components/organisms/fields/UserBadgeSelector';

export type ISearchCommunityFormMainSectionProps = IContainerProps & {
  form: UseFormReturn<ISearchCommunityFormBodyValues>;
  appId: string;
};

export const SearchCommunityFormMainSection: React.FC<
  ISearchCommunityFormMainSectionProps
> = (props) => {
  const { sx, form, appId } = props;
  const { t } = useTranslation();

  return (
    <FormSection sx={sx}>
      <Stack gap={4}>
        <Labeled
          label={t('SearchCommunityFormMainSection.fields.text.label')}
          supportingText={t(
            'SearchCommunityFormMainSection.fields.text.supportingText',
          )}
          disabled={form.watch('near')}
        >
          <FormField
            name="search"
            control={form.control}
            as={TextInputField}
            forwardError
            clearable
            type="search"
          />
        </Labeled>

        {form.watch('search') && form.watch('near') && (
          <div>
            <FontAwesomeIcon
              icon={faInfoCircle}
              style={{ marginRight: '0.25rem' }}
            />
            {t('SearchCommunityFormMainSection.fields.text.disabled')}
          </div>
        )}
      </Stack>

      <Labeled label={t('common.category')}>
        <FormField
          name="categoryId"
          control={form.control}
          as={CategorySelector}
          forwardError
          appId={appId}
        />
      </Labeled>

      <Labeled label={t('common.article')}>
        <FormField
          name="articleId"
          control={form.control}
          as={ArticleSelector}
          forwardError
          appId={appId}
          categoryId={form.watch('categoryId')}
          required={!!form.watch('categoryId')}
        />
      </Labeled>

      <Labeled label={t('SearchCommunityFormMainSection.fields.badges.label')}>
        <FormField
          name="onlyBadges"
          control={form.control}
          as={Select}
          items={[
            {
              value: '',
              label: t(
                'SearchCommunityFormMainSection.fields.badges.options.all',
              ),
            },
            {
              value: 'assigned',
              label: t(
                'SearchCommunityFormMainSection.fields.badges.options.assigned',
              ),
            },
            {
              value: 'requested',
              label: t(
                'SearchCommunityFormMainSection.fields.badges.options.requested',
              ),
            },
            {
              value: 'validated',
              label: t(
                'SearchCommunityFormMainSection.fields.badges.options.validated',
              ),
            },
            {
              value: 'rejected',
              label: t(
                'SearchCommunityFormMainSection.fields.badges.options.rejected',
              ),
            },
          ]}
          forwardError
        />
      </Labeled>

      <Labeled label={t('SearchCommunityFormMainSection.fields.badgeId.label')}>
        <FormField
          name="badgeId"
          control={form.control}
          as={UserBadgeSelector}
          appId={appId}
          forwardError
        />
      </Labeled>

      <Labeled label={t('SearchCommunityFormMainSection.fields.type.label')}>
        <FormField
          name="type"
          control={form.control}
          as={Select}
          items={[
            {
              value: 'usersAndDevices',
              label: t(
                'SearchCommunityFormMainSection.fields.type.options.usersAndDevices.label',
              ),
            },
            {
              value: 'users',
              label: t(
                'SearchCommunityFormMainSection.fields.type.options.users.label',
              ),
            },
            {
              value: 'devices',
              label: t(
                'SearchCommunityFormMainSection.fields.type.options.devices.label',
              ),
            },
          ]}
          forwardError
        />
      </Labeled>

      <Labeled
        label={t('SearchCommunityFormMainSection.fields.near.label')}
        labelPosition="right"
      >
        <FormField name="near" control={form.control} as={Checkbox} />
      </Labeled>
    </FormSection>
  );
};
